import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import {
  getEmailContent,
  listEmailDeliveries,
  resendEmail,
} from "../api/emails";
import { apiErrorMessage } from "../utils/apiError";

const ENTITY_TYPE = "ess_suggestion";

/** Cache key for one suggestion's delivery log. Also invalidated by
 *  `invalidateSuggestionCaches` — a status transition is what produces
 *  new emails, so the two always move together. */
export const emailDeliveryKey = (suggestionId: string) => [
  "ess-emails",
  suggestionId,
];

export function useEmailDeliveries(suggestionId: string) {
  return useQuery({
    queryKey: emailDeliveryKey(suggestionId),
    queryFn: () => listEmailDeliveries(ENTITY_TYPE, suggestionId),
    enabled: !!suggestionId,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

/** The stored body, fetched only once a row is opened. */
export function useEmailContent(emailId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: ["ess-email-content", emailId],
    queryFn: () => getEmailContent(emailId as string),
    enabled: !!emailId && enabled,
    staleTime: 10 * 60 * 1000,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

/**
 * Replay one recorded email. The backend writes a fresh attempt row
 * rather than mutating the old one, so success invalidates the list to
 * pull it in. A resend that reaches the server but fails to deliver
 * comes back with `status !== "sent"` — reported as a warning, not an
 * error, since the request itself worked.
 */
export function useResendEmail(suggestionId: string) {
  const qc = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (emailId: string) => resendEmail(emailId),
    onSuccess: (fresh) => {
      qc.invalidateQueries({ queryKey: emailDeliveryKey(suggestionId) });
      const ok = fresh?.status === "sent";
      enqueueSnackbar(
        ok ? "Email resent" : "Resend attempted but delivery failed",
        { variant: ok ? "success" : "warning" },
      );
    },
    onError: (err: unknown) => {
      enqueueSnackbar(apiErrorMessage(err, "Could not resend the email"), {
        variant: "error",
      });
    },
  });
}
